/* eslint-disable @typescript-eslint/no-explicit-any */
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import FocusTrap from '@mui/material/Unstable_TrapFocus';
import React from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { FormViewer } from '../FormViewer';
import { callConfigFunction } from '../WidgetForm/Utils';
import { useCommonFormUiTranslation } from '../hooks';
/**
 * Show a form in a dialog. User can submit the form or cancel.
 *
 * @template RootConfig - Root form configuration type, defaults to `WidgetFormConfig`.
 * @param {FormDialogParams<RootConfig>} params - Parameters that control form rendering, behavior, and callbacks.
 *
 * @example Show a form to input first name and last name
 * showFormDialog({
 *     title: 'User Information',
 *     config: {
 *         type: 'form',
 *         children: [
 *             {
 *                 type: 'form.text',
 *                 key: 'firstName',
 *                 label: 'First Name',
 *                 validate: {
 *                     required: true,
 *                 },
 *             },
 *             {
 *                 type: 'form.text',
 *                 key: 'lastName',
 *                 label: 'Last Name',
 *                 validate: {
 *                     required: true,
 *                 },
 *             },
 *         ],
 *     },
 *     onSubmit: (data) => {
 *         return saveDataToBackend(data, data.backendError)
 *             .then(() => {
 *                 showInfoSnackbar({ message: 'Form data was saved' });
 *             })
 *             .catch((error) => {
 *                 handleErrorDialog(error);
 *                 throw error; // rejecting a promise will keep the form dialog open
 *             });
 *     },
 * });
 */
export function showFormDialog(params) {
    const config = params.config;
    const internalState = {
        disabled: false,
    };
    showCustomDialog({
        maxWidth: params.maxWidth,
        fullWidth: params.fullWidth,
        content: (close) => (React.createElement(FormDialogContent, { params: params, data: config.onLoad ? callConfigFunction(config.onLoad, 'form onLoad', 'data', params.data) : params.data, internalSate: internalState, close: close })),
        onEscapeKeyDown: (close) => {
            if (!internalState.disabled) {
                close();
            }
        },
    });
}
function FormDialogContent({ params, data, internalSate, close, }) {
    var _a, _b;
    const formDialogBoxRef = React.useRef(null);
    const initialData = React.useRef(data);
    const [disabled, setDisabled] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showErrors, setShowErrors] = React.useState('changed');
    const [widgetData, setWidgetData] = React.useState();
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(Box, { ref: formDialogBoxRef, sx: { position: 'relative' } },
        React.createElement(DialogTitle, { "data-test": 'form-dialog-title' }, params.title),
        React.createElement(DialogContent, null,
            React.createElement(Box, { pt: 1, sx: params.sx },
                React.createElement(FormViewer, { config: params.config, data: initialData.current, widgets: params.widgets, globalWidgetConfig: params.globalWidgetConfig, valueProviders: params.valueProviders, subFormProviders: params.subFormProviders, rules: params.rules, formParams: params.formParams, showErrors: showErrors, onDataChange: setWidgetData, readOnly: params.readOnly }))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { color: 'primary', disabled: disabled, onClick: () => {
                    if (params.onCancel) {
                        params.onCancel();
                    }
                    close();
                }, "data-test": 'form-dialog-cancel-button' }, (_a = params.cancelLabel) !== null && _a !== void 0 ? _a : t('common.cancel')),
            !params.readOnly ? (React.createElement(Button, { color: 'primary', disabled: disabled, onClick: () => {
                    setShowErrors('all');
                    if (widgetData === null || widgetData === void 0 ? void 0 : widgetData.valid) {
                        const submit = () => {
                            const config = params.config;
                            const promise = params.onSubmit(config.onSubmit
                                ? callConfigFunction(config.onSubmit, 'form onSubmit', 'data', widgetData === null || widgetData === void 0 ? void 0 : widgetData.data)
                                : widgetData === null || widgetData === void 0 ? void 0 : widgetData.data);
                            if (promise) {
                                internalSate.disabled = true;
                                setDisabled(true);
                                const timeoutHandle = window.setTimeout(() => {
                                    setLoading(true);
                                }, 500);
                                promise
                                    .then(() => close())
                                    .catch(() => {
                                    internalSate.disabled = false;
                                    setDisabled(false);
                                    setLoading(false);
                                })
                                    .finally(() => window.clearTimeout(timeoutHandle));
                            }
                            else {
                                close();
                            }
                        };
                        if (params.onBeforeSubmit) {
                            params
                                .onBeforeSubmit(widgetData === null || widgetData === void 0 ? void 0 : widgetData.data)
                                .then((canSubmit) => {
                                if (canSubmit) {
                                    submit();
                                }
                            })
                                .catch(() => undefined);
                        }
                        else {
                            submit();
                        }
                    }
                    else {
                        window.setTimeout(() => {
                            if (formDialogBoxRef.current) {
                                const firstError = formDialogBoxRef.current.querySelector('[data-visible-error=true]');
                                if (firstError) {
                                    scrollIntoView(firstError, { scrollMode: 'if-needed', behavior: 'smooth' });
                                }
                            }
                        });
                    }
                }, "data-test": 'form-dialog-submit-button' }, (_b = params.submitLabel) !== null && _b !== void 0 ? _b : t('common.submit'))) : null),
        disabled && (React.createElement(FocusTrap, { open: true },
            React.createElement(Box, { tabIndex: -1, sx: {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    outline: 'none',
                    backgroundColor: loading ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                } }, loading && React.createElement(CircularProgress, { color: 'primary' }))))));
}
